/**
 * Decide whether ReorderUserParams should overwrite freshly-restored settings
 * with the balanced-preset DEFAULTS during `mounted()`.
 *
 * Only a brand-new user — one with no saved settings in EITHER the database or
 * localStorage — should get defaults. In `database` persistence mode nothing is
 * ever written to localStorage, so a guard that checks localStorage alone always
 * fires and clobbers the settings just loaded from the database (the saved
 * reorder preferences then never reach the buying table).
 */
export function shouldApplyDefaultReorderSettings(
  hasLoadedFromDatabase: boolean,
  hasLocalStorageSettings: boolean,
  databaseLoadFailed = false,
): boolean {
  // A failed database read returns null just like "no saved record", so it is
  // indistinguishable from a brand-new user unless we track the failure. Never
  // fall back to defaults after a failure — applying (and later persisting) them
  // would overwrite the saved settings we simply could not read.
  if (databaseLoadFailed) {
    return false;
  }
  return !hasLoadedFromDatabase && !hasLocalStorageSettings;
}

/**
 * Whether it is safe to write reorder settings to the database. Blocks writes
 * while a read is in flight (`isDatabaseLoading`) or after a failed read
 * (`databaseLoadFailed`): in both states we don't have a trustworthy view of the
 * stored record, so persisting could overwrite settings we couldn't read.
 */
export function canPersistReorderToDatabase(
  isDatabaseLoading: boolean,
  databaseLoadFailed: boolean,
): boolean {
  return !isDatabaseLoading && !databaseLoadFailed;
}
